import { CircularProgress, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import  { useState } from "react";
import { ReactComponent as RoleIcon } from '../../../../svg/adminRoleIcon.svg';
import { ReactComponent as Arrow } from '../../../../svg/arrow.svg';
import SettingsModal from '../Modal/settingsCustomModal';
import apiCall from "../../../../utils/apiCall";
import ApprovalTypeDetails from "./ApprovalTypeDetails";
import ApprovalTypeOptions from "./ApprovalTypeOptions";
import UpdateApprovalType from "./UpdateApprovalType";
// import DeleteApprovalType from "./DeleteApprovalType";
import { AxiosError } from "axios";

interface ApprovalType {
  ApprovalTypeId: number;
  ApprovalTypName: string;
};

interface ApprovalTypeResponse {
    data: ApprovalType[];
}

const ApprovalTypeContent = () => {
    const [openApprovalTypeDetails, setOpenApprovalTypeDetails] = useState(false);
    const [openUpdateApprovalTypeModal, setOpenUpdateApprovalTypeModal] = useState(false);
    const [openDeleteApprovalTypeModal, setOpenDeleteApprovalTypeModal] = useState(false);
    const [approvalTypeName, setApprovalTypeName] = useState('');
    const [approvalTypeId, setApprovalTypeId] = useState(0);

    const handleOpenApprovalTypeDetails = () => setOpenApprovalTypeDetails(true);
    const handleCloseApprovalTypeDetails = () => setOpenApprovalTypeDetails(false);

    const handleOpenUpdateApprovalTypeModal = () => setOpenUpdateApprovalTypeModal(true);
    const handleCloseUpdateApprovalTypeModal = () => setOpenUpdateApprovalTypeModal(false);

    const handleOpenDeleteApprovalTypeModal = () => setOpenDeleteApprovalTypeModal(true);
    const handleCloseDeleteApprovalTypeModal = () => setOpenDeleteApprovalTypeModal(false);

    const handleDelete = () => {
        handleCloseApprovalTypeDetails();
        handleOpenDeleteApprovalTypeModal();
    };
    
    const handleUpdate = () => {
        handleCloseApprovalTypeDetails();
        handleOpenUpdateApprovalTypeModal();
    };

    const handleOpen = (approvalType: ApprovalType) => {
        setApprovalTypeId(approvalType?.ApprovalTypeId);
        setApprovalTypeName(approvalType?.ApprovalTypName);
        handleOpenApprovalTypeDetails();
    }

    const getApprovalTypes = async (): Promise<ApprovalTypeResponse> => {
        return (await apiCall().get('/ApprovalType/GetApprovalTypes')); 
    };

    const { data, isLoading, isError, error } = useQuery(['get-approval-types'], getApprovalTypes, {
        retry: 0,
        keepPreviousData: true,
        enabled: true,
    });

    const getApprovalTypesMessage = error as AxiosError;
    const getApprovalTypesErrorMessage = getApprovalTypesMessage?.message;

    return (
        <>
            {isLoading ? (
                <CircularProgress 
                    sx={{
                        fontSize: '1rem',
                        marginInline: 'auto',
                        display: 'block',
                        marginBlock: '2rem',
                    }}
                />
            ) : isError ? (
                <StyledError>{getApprovalTypesErrorMessage}</StyledError>
            ) : (
            <StyledContainer>
                <TotalWrapper>
                    <span>Total</span>
                    <TotalNumberStyle>{data?.data?.length}</TotalNumberStyle>
                </TotalWrapper>
                {data?.data && data?.data?.length > 0 &&
                    data?.data.map((data, index) => (
                    <RoleWrapper onClick={() => handleOpen(data)} key={index}>
                        <div>
                            <RoleIconWrapper><RoleIcon /></RoleIconWrapper>
                            <RoleText>{data?.ApprovalTypName}</RoleText>
                            <Active>Active</Active>
                            <DateWrapper></DateWrapper>
                        </div>
                        <ArrowIconWrapper>
                            <Arrow />
                        </ArrowIconWrapper>
                    </RoleWrapper>
                ))}

                <SettingsModal 
                    open={openApprovalTypeDetails}
                    cancel={handleCloseApprovalTypeDetails}
                    maxWidth='712px'
                    header={approvalTypeName}
                    content={<ApprovalTypeDetails id={approvalTypeId} name={approvalTypeName}/>}
                    card={<ApprovalTypeOptions 
                        handleUpdate={handleUpdate} 
                        handleDelete={handleDelete}
                    />}
                />

                <SettingsModal
                    open={openUpdateApprovalTypeModal}
                    cancel={handleCloseUpdateApprovalTypeModal}
                    maxWidth='480px'
                    header='Update Approval Type'
                    showrightIcon={false}
                    content={<UpdateApprovalType 
                        approvalTypeId={approvalTypeId} 
                        handleClose={handleCloseUpdateApprovalTypeModal} 
                        approvalTypeName={approvalTypeName}
                    />}   
                />
{/* 
                <SettingsModal
                    open={openDeleteApprovalTypeModal}
                    cancel={handleCloseDeleteApprovalTypeModal}
                    maxWidth='653px'
                    header='Delete Approval Type'
                    showrightIcon={false}
                    content={<DeleteApprovalType 
                        approvalTypeId={approvalTypeId} 
                        approvalTypeName={approvalTypeName} 
                        handleClose={handleCloseDeleteApprovalTypeModal}
                    />}   
                /> */}
            </StyledContainer>
            )}
        </>       
    )
};

export default ApprovalTypeContent;

const StyledContainer = styled('div')`
    margin-block: 3.8rem;
    margin-inline: 3.5rem;
`

const TotalNumberStyle = styled('span')`
    background-color: #F1F3F4;
    border-radius: 0.2rem;
    margin-inline-start: 0.4rem;
    padding-inline: 0.2rem;
`

const TotalWrapper = styled('div')`
    color: #4E5A62;
    font-weight: 500;
`

const RoleWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    border: 0.1rem solid #E1E1E1;
    margin-block: 0.6rem;
    padding-inline: 1.2rem;
    padding-block: 1.2rem;
    border-radius: 0.5rem;
    width: 100%;
    text-align: left;
    background-color: transparent;
`

const RoleIconWrapper = styled('span')`
    background-color: #FFF4DE;
    border-radius: 0.1rem;
    padding-inline: 0.4rem;
`

const RoleText = styled('span')`
    font-weight: 600;
    font-size: 16px;
    margin-inline: 0.8rem;
`

const Active = styled('span')`
    font-size: 14px;
    font-weight: 500;
    background-color: #E0FAEE;
    border-radius: 0.4rem;
    padding-inline: 0.5rem;
    color: #11643C;
`

const DateWrapper = styled('div')`
    margin-block-start: 0.8rem;
`

const ArrowIconWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`